
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { getCookieValue } from "@/utils/cookie-utils";

// Event types following Facebook Conversions API standards
export type FacebookEventName = 
  | "PageView"
  | "ViewContent" 
  | "Lead" 
  | "CompleteRegistration"
  | "InitiateCheckout"
  | "Purchase"
  | "Subscribe";

interface FacebookEventOptions {
  customData?: {
    currency?: string;
    value?: number;
    contentName?: string;
    contentIds?: string[];
    contents?: Array<{id: string; quantity: number}>;
    contentType?: string;
    orderId?: string;
    [key: string]: any;
  };
  userData?: {
    email?: string;
    phone?: string;
    externalId?: string;
    sourceUrl?: string;
    fbp?: string; // Facebook browser pixel cookie
    fbc?: string; // Facebook click ID cookie
  };
}

export const useFacebookConversions = () => {
  const { user } = useAuth();

  // Get Facebook cookie values if available
  const getFacebookCookies = () => {
    return {
      fbp: getCookieValue('_fbp'),
      fbc: getCookieValue('_fbc')
    };
  };

  // Track event to Facebook Conversions API
  const trackEvent = useCallback(async (
    eventName: FacebookEventName,
    options: FacebookEventOptions = {}
  ) => {
    try {
      // Get Facebook cookie values for better matching
      const { fbp, fbc } = getFacebookCookies();
      
      // Prepare user data
      const userData = {
        ...options.userData,
        fbp,
        fbc
      };

      // Add user email if authenticated
      if (user?.email && !userData.email) {
        userData.email = user.email;
      }

      // Add user ID if authenticated
      if (user?.id && !userData.externalId) {
        userData.externalId = user.id;
      }

      console.log(`Tracking Facebook event: ${eventName}`, {
        eventName,
        userData: {
          ...userData,
          // Mask email for privacy in logs
          email: userData.email ? 
            userData.email.substring(0, 2) + "..." + 
            userData.email.split("@")[1] : undefined
        },
        customData: options.customData
      });

      // Call the edge function
      const { data, error } = await supabase.functions.invoke("facebook-conversions", {
        body: {
          event: eventName,
          userData,
          customData: options.customData
        }
      });

      if (error) {
        console.error("Error tracking Facebook event:", error);
        return { success: false, error };
      }

      return { success: true, data };
    } catch (error) {
      console.error("Error tracking Facebook event:", error);
      return { success: false, error };
    }
  }, [user]);

  return { trackEvent };
};

export default useFacebookConversions;
